import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { MatterList } from './components/matter-list';
import { WhereYouLeftOff } from './components/where-you-left-off';
import { Permission } from '../../../../../backend/src/auth/permissions/permission';
import { useImportSampleMatter } from '../../../api/mutations/useImportSampleMatter';
import { SampleMatterLoader } from '../../components/sample-matter-loader';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateMatter } from '@/api/mutations/useCreateMatter';
import { useGetFiles } from '@/api/queries/useGetFiles';
import { useGetGlobalRecentSubsets } from '@/api/queries/useGetGlobalRecentSubsets';
import { useGetMatters } from '@/api/queries/useGetMatters';
import { Matter } from '@/api/types/backend-dto-types';
import FolderGlass from '@/assets/img/folder-glass.jpeg';
import formatDate from '@/helpers/formatDate';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { CreditBlockTooltip } from '@/new/components/credit-block-tooltip';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';

export function Matters() {
  const { t } = useTranslation();
  const { data: matters, isLoading: isLoadingMatters } = useGetMatters();
  const { data: recentSubsets } = useGetGlobalRecentSubsets();

  const nonDraftMatters = matters?.filter((matter) => !matter.isDraft);
  const draftMatters = matters?.filter((matter) => matter.isDraft);
  const hasMatters = (nonDraftMatters?.length ?? 0) > 0;
  const hasDraftMattersOnly = !hasMatters && (draftMatters?.length ?? 0) > 0;

  const { mutateAsync: createMatter, isLoading } = useCreateMatter();
  const { mutateAsync: importSampleMatter, isLoading: isImportingSampleMatter } = useImportSampleMatter();

  const navigate = useNavigate();

  const handleCreateMatter = async () => {
    await createMatter(undefined, {
      onSuccess: (data) => {
        trackCustomEvent(TrackingEventsV2.MatterCreate, { matterId: data.id });
        navigate(`/matters/new/${data.id}`);
      },
    });
  };

  const handleCreateSampleMatter = async (context: 'empty_state' | 'draft_only') => {
    trackCustomEvent(TrackingEventsV2.MatterCreateSample, { source: 'matters_list', context });
    await importSampleMatter(undefined, {
      onSuccess: (data) => {
        navigate(`/matters/${data.matterId}`);
      },
    });
  };

  return (
    <>
      <Breadcrumb items={[{ href: '/matters', label: 'Matters', active: true }]} />
      <PageContainer className="h-full">
        {isLoadingMatters ? (
          <div className="flex h-full items-center justify-center">
            <Icon className="lucide--loader animate-spin text-gray-400 ease-in-out" />
          </div>
        ) : hasMatters ? (
          <>
            <PageHeader>
              <PageTitle text="Matters" />
              <PermissionGate permission={Permission.MatterCreate} uiMode="disabled">
                <CreditBlockTooltip side="left">
                  <Button variant="primary" onClick={handleCreateMatter} data-tour="create-matter-btn">
                    <Icon className="lucide--folder-plus h-4 w-4" />
                    Create a New Matter
                  </Button>
                </CreditBlockTooltip>
              </PermissionGate>
            </PageHeader>

            <div className="space-y-6 pb-1.5" data-tour="matters-list">
              {recentSubsets && recentSubsets.length > 0 && <WhereYouLeftOff recentSubsets={recentSubsets} />}
              <MatterList matters={matters} />
            </div>
          </>
        ) : (
          <AnimatePresence mode="wait">
            {isImportingSampleMatter ? (
              <SampleMatterLoader />
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 40 }}
                transition={{ duration: 0.3, ease: 'easeIn' }}
                className="h-full"
              >
                {hasDraftMattersOnly ? (
                  <EmptyStateLayout title="Finish creating your first Matter">
                    <div className="mt-6 w-full max-w-sm space-y-2">
                      {draftMatters!.map((matter) => (
                        <DraftMatterCard key={matter.id} matter={matter} />
                      ))}
                    </div>

                    <Button
                      variant={'ghost'}
                      onClick={() => handleCreateSampleMatter('draft_only')}
                      disabled={isImportingSampleMatter}
                      className="group mt-6 mb-2 ring-offset-0 duration-150 focus-visible:ring-offset-2"
                    >
                      Browse a sample Matter instead
                      <Icon className="lucide--chevron-right duration-150 group-hover:translate-x-0.5" />
                    </Button>
                  </EmptyStateLayout>
                ) : (
                  <EmptyStateLayout title="Create your first Matter.">
                    <p className="mt-4 mb-8 max-w-md text-balance text-gray-600">
                      Try uploading {t('witness statements')}, emails, or contracts. Wexler will extract the facts and
                      allow you to query your entire document set.
                    </p>

                    <PermissionGate permission={Permission.MatterCreate} uiMode="disabled">
                      <CreditBlockTooltip side="right">
                        <Button
                          variant={'primary'}
                          onClick={handleCreateMatter}
                          disabled={isLoading}
                          className="group shadow-lg/10 shadow-teal-500 ring-offset-0 transition-all duration-150 hover:bg-teal-50 hover:shadow-lg/15 focus-visible:ring-offset-2"
                        >
                          Start by uploading some documents{' '}
                          <Icon className="lucide--chevron-right duration-150 group-hover:translate-x-0.5" />
                        </Button>
                      </CreditBlockTooltip>
                    </PermissionGate>

                    <Button
                      variant={'ghost'}
                      onClick={() => handleCreateSampleMatter('empty_state')}
                      disabled={isImportingSampleMatter}
                      className="group mt-4 mb-12 ring-offset-0 duration-150 focus-visible:ring-offset-2"
                    >
                      New to Wexler? Browse a sample Matter instead
                      <Icon className="lucide--chevron-right duration-150 group-hover:translate-x-0.5" />
                    </Button>
                  </EmptyStateLayout>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </PageContainer>
    </>
  );
}

function EmptyStateLayout({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="-mt-14 flex h-full flex-col items-center justify-center gap-1">
      <img src={FolderGlass} alt="" className="size-22 scale-x-112 rotate-3 -hue-rotate-15" />
      <h2 className="mt-5 font-serif text-3xl font-medium">{title}</h2>
      {children}
      <p className="text-xs text-gray-500">Colleagues already using Wexler? Ask to be added to an existing Matter.</p>
    </div>
  );
}

function DraftMatterCard({ matter }: { matter: Matter }) {
  const { data: files } = useGetFiles(matter.id);
  const filesCount = files?.length ?? 0;

  return (
    <Button
      asChild
      variant={'ghost'}
      className="group flex w-full items-center justify-between rounded-lg bg-white px-4 py-3 text-left duration-150 hover:bg-gray-50"
    >
      <Link to={`/matters/new/${matter.id}`}>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-gray-900">{matter.name || 'Unnamed Matter'}</p>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-500">
            <span>
              {filesCount} {filesCount === 1 ? 'file' : 'files'}
            </span>{' '}
            · <span>Created {formatDate(matter.createdDate, true)}</span>
          </p>
        </div>
        <Icon className="lucide--arrow-right mr-2 flex-shrink-0 text-base text-gray-400 duration-150 group-hover:translate-x-1 group-hover:text-teal-500" />
      </Link>
    </Button>
  );
}
